import React, { ChangeEventHandler, useCallback, useRef, useState } from "react";

import { useMutation } from "@apollo/client";
import { FieldValues, UseFormSetError, Path, PathValue, UseFormSetValue } from "react-hook-form";

import { Button, HStack, Input, InputProps as ChakraInputProps, StackProps, Text } from "@chakra-ui/react";

import { graphql } from "@/__generated__";
import useReadySearchParams from "@/hooks/useReadySearchParams";

const MJobParameters_DirectUploadCreate = graphql(`
  mutation MJobParameters_DirectUploadCreate(
    $workspaceId: ID!
    $applicationId: ID!
    $filename: String!
    $contentType: String!
  ) {
    directUploadsCreate(
      input: {
        workspaceId: $workspaceId
        appId: $applicationId
        filename: $filename
        contentType: $contentType
      }
    ) {
      directUpload {
        presignedUrl
        s3Uri
      }
    }
  }
`);

type InputProps = Omit<ChakraInputProps, "hidden" | "type"> & {
  ref?: React.RefCallback<HTMLInputElement | null> | React.MutableRefObject<HTMLInputElement | null>;
};

type Props<TFieldValues extends FieldValues> = {
  inputProps?: InputProps;
  ref?: React.Ref<HTMLInputElement | null>;
  directUpload?: boolean;
  hasErrors?: boolean;
} & (
  | {
      directUpload: true;
      formName: Path<TFieldValues>;
      setError: UseFormSetError<TFieldValues>;
      setValue: UseFormSetValue<TFieldValues>;
    }
  | {
      directUpload?: false;
      formName?: Path<TFieldValues>;
      setError?: UseFormSetError<TFieldValues>;
      setValue?: UseFormSetValue<TFieldValues>;
    }
);

async function uploadFile(url: string, file: File) {
  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (e) {
    const error = e as unknown as Error;
    if (error.message === "Failed to fetch") {
      return {
        error: "CORS/Network",
        message: "A CORS or network error occurred. Check CORS configuration and network connectivity.",
      };
    } else if (error.name === "AbortError") {
      return { error: "Abort", message: "Fetch request was aborted" };
    } else {
      return { error: "Unknown", message: `An error occurred: ${error.message}` };
    }
  }
}
export default function FilePicker<TFieldValues extends FieldValues>({
  onChange,
  inputProps = {},
  directUpload = false,
  formName,
  setError,
  setValue,
  hasErrors = false,
  ...stackProps
}: Props<TFieldValues> & StackProps) {
  const [fileName, setFileName] = useState("");
  const internalRef = useRef<HTMLInputElement | null>(null);
  const { workspaceId, applicationId } = useReadySearchParams();
  const [directUploadsCreate] = useMutation(MJobParameters_DirectUploadCreate);

  const handleDirectUpload: ChangeEventHandler<HTMLInputElement> = useCallback(
    async event => {
      if (!event.target.files) return;
      const file = event.target.files[0];
      setFileName("Uploading...");

      const response = await directUploadsCreate({
        variables: {
          workspaceId,
          applicationId,
          filename: file.name,
          contentType: file.type,
        },
      });

      if (response.data?.directUploadsCreate?.directUpload) {
        const url = response.data.directUploadsCreate.directUpload.presignedUrl;
        const uploadData = await uploadFile(url, file);
        if (!uploadData?.error) {
          if (response.data?.directUploadsCreate?.directUpload) {
            const s3Uri = response.data.directUploadsCreate.directUpload.s3Uri;
            setFileName(s3Uri);
            setValue &&
              formName &&
              setValue(formName, s3Uri as PathValue<TFieldValues, Path<TFieldValues>>, {
                shouldValidate: true,
              });
          }
        } else {
          setFileName("");
          if (internalRef.current) {
            internalRef.current.value = "";
          }
          setError &&
            formName &&
            setError(formName, { type: "custom", message: uploadData?.message }, { shouldFocus: true });
        }
      }
    },
    [applicationId, directUploadsCreate, formName, setError, setValue, workspaceId]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files?.length) {
      setFileName(files[0].name);
    } else {
      setFileName("");
    }
    inputProps.onChange && inputProps.onChange(event);
  };

  const handleClick = () => {
    if (!internalRef.current) return;

    internalRef.current.click();
  };

  const setInputRef = (element: HTMLInputElement | null) => {
    // Assign to react-hook-form's ref if it exists
    if (typeof inputProps.ref === "function") {
      inputProps.ref(element);
    } else if (inputProps.ref && "current" in inputProps.ref) {
      inputProps.ref.current = element;
    }

    internalRef.current = element;
  };

  const erroredAttrs = hasErrors ? { border: "2px", borderColor: "red", borderRadius: "10px" } : {};
  // We manually set required to false because the input is hidden and it cannot be focused. Instead, the error must be handled by the caller.
  return (
    <HStack alignItems="center" spacing={4} {...stackProps} {...erroredAttrs}>
      <Input
        {...inputProps}
        ref={setInputRef}
        hidden
        isRequired={false}
        type="file"
        onChange={directUpload ? handleDirectUpload : handleFileChange}
      />
      <Button size="action" onClick={handleClick}>
        Choose File
      </Button>
      <Text ml={2}>{fileName || "No file chosen"}</Text>
    </HStack>
  );
}
